import { TFunction } from "@einride/saga-core"
import { Group, Icon, Table, Tbody, Td, Th, Thead, Tr, useTheme } from "@einride/ui"
import styled from "@emotion/styled"
import { PushPinSimple } from "@phosphor-icons/react"
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { formatPlainDate, getDefaultDateFormat } from "components/format/PlainDate/PlainDate"
import { useUserPreferences } from "components/UserPreferences/UserPreferencesContext"
import { ShipmentLegFragment } from "gen/graphql"
import { DateTime } from "luxon"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

function createColumns(
  locale: string,
  t: TFunction<"explore">,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<ShipmentLegFragment, any>[] {
  const deliveryReportedCompleteAtAccessor = (shipmentLeg: ShipmentLegFragment): string => {
    const at = shipmentLeg.deliveryReportedCompleteAt
    if (at?.timestamp) {
      const format = `${getDefaultDateFormat(locale)} hh:mm`
      return formatPlainDate(DateTime.fromISO(shipmentLeg.deliveryReportedCompleteTime), { format })
    }
    if (at?.date) {
      const format = `${getDefaultDateFormat(locale)}`
      const dateTime = {
        year: at.date.year,
        month: at.date.month,
        day: at.date.day,
      }
      return formatPlainDate(DateTime.fromObject(dateTime), { format })
    }
    return "–"
  }

  const columnHelper = createColumnHelper<ShipmentLegFragment>()
  return [
    columnHelper.accessor("shipment", {
      header: () => t("shipmentLegsPage.shipmentId"),
      cell: (info) => {
        const reference = info.getValue()
        const parts = reference.split("/")
        return parts[parts.length - 1]
      },
      size: 300,
    }),
    columnHelper.accessor("shipmentExternalReferenceId", {
      header: () => t("shipmentLegsPage.referenceId"),
      cell: (info) => info.getValue(),
      size: 300,
    }),
    columnHelper.accessor(
      (shipmentLeg: ShipmentLegFragment) => {
        const format = `${getDefaultDateFormat(locale)} hh:mm`
        return shipmentLeg.shipmentReleaseTime
          ? formatPlainDate(DateTime.fromISO(shipmentLeg.shipmentReleaseTime), {
              format,
            })
          : "–"
      },
      {
        id: "shipmentReleaseTime",
        header: () => t("shipmentLegsPage.releasedAt"),
        size: 220,
      },
    ),
    columnHelper.accessor(deliveryReportedCompleteAtAccessor, {
      id: "deliveryReportedCompleteAt",
      header: () => t("shipmentLegsPage.deliveredAt"),
      size: 220,
    }),
    columnHelper.accessor("originLocationDisplayName", {
      header: () => t("shipmentLegsPage.originLocation"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("originCountryCode", {
      header: () => t("shipmentLegsPage.originCountry"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("originCity", {
      header: () => t("shipmentLegsPage.originCity"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("originPostalCode", {
      header: () => t("shipmentLegsPage.originPostalCode"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("originLocationDisplayName", {
      header: () => t("shipmentLegsPage.originLocation"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("destinationCountryCode", {
      header: () => t("shipmentLegsPage.destinationCountry"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("destinationCity", {
      header: () => t("shipmentLegsPage.destinationCity"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("destinationPostalCode", {
      header: () => t("shipmentLegsPage.destinationPostalCode"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
    columnHelper.accessor("vehicleDriveType", {
      header: () => t("shipmentLegsPage.driveType"),
      cell: (info) => info.getValue(),
      size: 200,
    }),
    columnHelper.accessor("vehicleEnergySource", {
      header: () => t("shipmentLegsPage.energySource"),
      cell: (info) => info.getValue(),
      size: 256,
    }),
  ]
}

interface TableProps {
  shipmentLegs: ShipmentLegFragment[]
  footer: ReactNode
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
}

export const TableData = ({
  shipmentLegs,
  footer,
  sorting,
  setSorting,
}: TableProps): React.JSX.Element => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { locale } = useUserPreferences()
  const [columns] = useState(() => createColumns(locale, t))
  const [columnPinning, setColumnPinning] = useState<{ left?: string[]; right?: string[] }>({
    left: ["shipment"],
    right: [],
  })

  const table = useReactTable({
    data: shipmentLegs,
    columns,
    state: {
      sorting,
      columnPinning,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <>
      <Container>
        {table.getLeftHeaderGroups()[0].headers.length > 0 && (
          <Left>
            <StyledTable
              {...{
                style: {
                  minWidth: "100%",
                  width: table.getLeftTotalSize(),
                },
              }}
            >
              <StyledThead>
                {table.getLeftHeaderGroups().map((headerGroup) => (
                  <HoverableTr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <StyledTh
                          key={header.id}
                          textAlign="start"
                          scope="col"
                          onClick={header.column.getToggleSortingHandler()}
                          style={{ width: header.getSize() }}
                          color="secondary"
                        >
                          <Group justifyContent="space-between" flexWrap="nowrap">
                            <div>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: <SortedIcon name="arrowUp" />,
                                desc: <SortedIcon name="arrowDown" />,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                            <StyledPin
                              color={theme.colors.background.primaryInverted}
                              weight="fill"
                              onClick={(e) => {
                                e.stopPropagation()
                                header.column.pin(false)
                              }}
                            />
                          </Group>
                        </StyledTh>
                      )
                    })}
                  </HoverableTr>
                ))}
              </StyledThead>
              <Tbody>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <HoverableTr key={row.id}>
                      {row.getLeftVisibleCells().map((cell) => {
                        return (
                          <StyledTd key={cell.id} nowrap>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </StyledTd>
                        )
                      })}
                    </HoverableTr>
                  )
                })}
              </Tbody>
            </StyledTable>
          </Left>
        )}
        <Right>
          <StyledTable
            {...{
              style: {
                minWidth: "100%",
                width: table.getCenterTotalSize(),
              },
            }}
          >
            <StyledThead>
              {table.getCenterHeaderGroups().map((headerGroup) => (
                <HoverableTr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <StyledTh
                        key={header.id}
                        textAlign="start"
                        scope="col"
                        onClick={header.column.getToggleSortingHandler()}
                        style={{ width: header.getSize() }}
                        color="secondary"
                      >
                        <Group justifyContent="space-between" flexWrap="nowrap">
                          <div>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <SortedIcon name="arrowUp" />,
                              desc: <SortedIcon name="arrowDown" />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                          <StyledPin
                            onClick={(e) => {
                              e.stopPropagation()
                              header.column.pin("left")
                            }}
                          />
                        </Group>
                      </StyledTh>
                    )
                  })}
                </HoverableTr>
              ))}
            </StyledThead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <HoverableTr key={row.id}>
                    {row.getCenterVisibleCells().map((cell) => {
                      return (
                        <StyledTd key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </StyledTd>
                      )
                    })}
                  </HoverableTr>
                )
              })}
            </Tbody>
          </StyledTable>
        </Right>
      </Container>
      {footer}
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
`

const SortedIcon = styled(Icon)`
  margin-left: 0.5rem;
`

const StyledTable = styled(Table)`
  table-layout: fixed;
`

const StyledTh = styled(Th)<{ textAlign: "start" | "end" }>`
  text-align: ${({ textAlign }) => textAlign};
`

const StyledThead = styled(Thead)`
  cursor: pointer;
  position: sticky;
  top: 0;
`

const HoverableTr = styled(Tr)`
  :hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`

const StyledTd = styled(Td)<{ nowrap?: boolean }>`
  padding-block-start: 1rem;
  padding-block-end: 1rem;
  word-break: break-all;
  ${({ nowrap }) =>
    nowrap
      ? `overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;`
      : undefined}
`

const Left = styled.div`
  display: flex;
  align-items: flex-start;
  position: sticky;
  text-align: left;
  border-right: 0.0625rem solid ${({ theme }) => theme.colors.border.primary};
`

const Right = styled.div`
  overflow-x: auto;
  width: 100%;
`

const StyledPin = styled(PushPinSimple)`
  :hover {
    opacity: 0.5;
  }
`
